import { Box, Button, Card, Group, Modal, NumberInput, Stack, Table, Text, TextInput, Title, Select, Switch, Badge, Tabs } from '@mantine/core';
import { formatDateTime, formatDate } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCreditCard, IconFileInvoice, IconReceipt, IconDownload } from '@tabler/icons-react';
import { useState, type JSX } from 'react';
import { InfoSection } from '../../components/InfoSection';

interface PaymentMethod {
  id: number;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

interface Invoice {
  id: number;
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  type: string;
  description: string;
  paidDate?: string;
  paidAmount?: number;
  paymentMethod?: string;
}

interface PaymentHistory {
  id: number;
  date: string;
  amount: number;
  type: 'payment' | 'refund' | 'credit';
  description: string;
  paymentMethod: string;
  status: 'completed' | 'pending' | 'failed';
}

interface InsuranceInfo {
  provider: string;
  policyNumber: string;
  groupNumber: string;
  effectiveDate: string;
  copay: number;
  deductible: number;
  deductibleMet: number;
}

export function MembershipAndBilling(): JSX.Element {
  const medplum = useMedplum();
  const patient = medplum.getProfile() as Patient;
  const [showAddCard, setShowAddCard] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('invoices');

  const paymentMethods: PaymentMethod[] = [
    { id: 1, last4: '4242', brand: 'Visa', expMonth: 12, expYear: 2024, isDefault: true },
    { id: 2, last4: '1234', brand: 'Mastercard', expMonth: 8, expYear: 2025, isDefault: false }
  ];

  const invoices: Invoice[] = [
    { 
      id: 1, 
      date: '2024-03-10', 
      amount: 150, 
      status: 'paid', 
      type: 'Therapy Session',
      description: 'Individual Therapy (50 min)',
      paidDate: '2024-03-10',
      paidAmount: 150,
      paymentMethod: 'Visa ending in 4242'
    },
    { 
      id: 2, 
      date: '2024-03-03', 
      amount: 150, 
      status: 'paid', 
      type: 'Therapy Session',
      description: 'Individual Therapy (50 min)',
      paidDate: '2024-03-03',
      paidAmount: 150,
      paymentMethod: 'Visa ending in 4242'
    },
    { 
      id: 3, 
      date: '2024-03-17', 
      amount: 150, 
      status: 'pending', 
      type: 'Therapy Session',
      description: 'Individual Therapy (50 min)'
    }
  ];

  const paymentHistory: PaymentHistory[] = [
    {
      id: 1,
      date: '2024-03-10',
      amount: 150,
      type: 'payment',
      description: 'Payment for session on Mar 10',
      paymentMethod: 'Visa ending in 4242',
      status: 'completed'
    },
    {
      id: 2,
      date: '2024-03-03',
      amount: 150,
      type: 'payment',
      description: 'Payment for session on Mar 3',
      paymentMethod: 'Visa ending in 4242',
      status: 'completed'
    }
  ];

  const insuranceInfo: InsuranceInfo = {
    provider: 'Blue Cross Blue Shield',
    policyNumber: 'XYZ123456789',
    groupNumber: 'GROUP123',
    effectiveDate: '2024-01-01',
    copay: 25,
    deductible: 1500,
    deductibleMet: 500
  };

  const handlePayment = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  // Toggle AutoPay is handled on the provider side only

  return (
    <Box p="xl">
      <Title mb="xl">Billing & Payments</Title>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="invoices">Invoices & Payments</Tabs.Tab>
          <Tabs.Tab value="payment-methods">Payment Methods</Tabs.Tab>
          <Tabs.Tab value="insurance">Insurance</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="invoices">
          <Stack>
            <InfoSection title="Recent Invoices">
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Service</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {invoices.map((invoice) => (
                    <Table.Tr key={invoice.id}>
                      <Table.Td>{formatDate(invoice.date)}</Table.Td>
                      <Table.Td>{invoice.type}</Table.Td>
                      <Table.Td>{invoice.description}</Table.Td>
                      <Table.Td>${invoice.amount.toFixed(2)}</Table.Td>
                      <Table.Td>
                        <Badge 
                          color={
                            invoice.status === 'paid' 
                              ? 'green' 
                              : invoice.status === 'pending' 
                              ? 'yellow' 
                              : 'red'
                          }
                        >
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group>
                          {invoice.status === 'pending' && (
                            <Button variant="light" onClick={() => handlePayment(invoice)}>
                              Pay Now
                            </Button>
                          )}
                          <Button variant="subtle">
                            <Group gap={4}>
                              <IconFileInvoice size={16} />
                              <span>View Invoice</span>
                            </Group>
                          </Button>
                          <Button variant="subtle">
                            <Group gap={4}>
                              <IconReceipt size={16} />
                              <span>Superbill</span>
                            </Group>
                          </Button>
                          <Button variant="subtle">
                            <Group gap={4}>
                              <IconDownload size={16} />
                              <span>Download</span>
                            </Group>
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </InfoSection>

            <InfoSection title="Payment History">
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Payment Method</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paymentHistory.map((payment) => (
                    <Table.Tr key={payment.id}>
                      <Table.Td>{formatDate(payment.date)}</Table.Td>
                      <Table.Td>{payment.description}</Table.Td>
                      <Table.Td>${payment.amount.toFixed(2)}</Table.Td>
                      <Table.Td>{payment.paymentMethod}</Table.Td>
                      <Table.Td>
                        <Badge 
                          color={
                            payment.status === 'completed' 
                              ? 'green' 
                              : payment.status === 'pending' 
                              ? 'yellow' 
                              : 'red'
                          }
                        >
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </InfoSection>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="payment-methods">
          <InfoSection title="Payment Methods">
            <Text size="sm" color="dimmed" mb="md">
              Your provider may enroll you in AutoPay for automatic billing. If you have questions about your enrollment status or wish to change it, please contact your provider directly.
            </Text>
            <Stack>
              {paymentMethods.map((method) => (
                <Card key={method.id} withBorder p="md">
                  <Group justify="space-between">
                    <Group>
                      <IconCreditCard size={24} />
                      <div>
                        <Group>
                          <Text>
                            {method.brand} ending in {method.last4}
                          </Text>
                          {method.isDefault && (
                            <Badge color="blue">Default</Badge>
                          )}
                        </Group>
                        <Text size="sm" color="dimmed">
                          Expires {method.expMonth}/{method.expYear}
                        </Text>
                      </div>
                    </Group>
                    <Group>
                      {!method.isDefault && (
                        <Button variant="light">Set as Default</Button>
                      )}
                      <Button variant="subtle" color="red">
                        Remove
                      </Button>
                    </Group>
                  </Group>
                </Card>
              ))}
              <Button onClick={() => setShowAddCard(true)}>
                <Group gap={4}>
                  <IconCreditCard size={16} />
                  <span>Add Payment Method</span>
                </Group>
              </Button>
            </Stack>
          </InfoSection>
        </Tabs.Panel>

        <Tabs.Panel value="insurance">
          <InfoSection title="Insurance Information">
            <Stack gap="md">
              <Group justify="space-between">
                <div>
                  <Text fw={500}>Insurance Provider</Text>
                  <Text>{insuranceInfo.provider}</Text>
                </div>
                <div>
                  <Text fw={500}>Policy Number</Text>
                  <Text>{insuranceInfo.policyNumber}</Text>
                </div>
                <div>
                  <Text fw={500}>Group Number</Text>
                  <Text>{insuranceInfo.groupNumber}</Text>
                </div>
              </Group>
              
              <Group justify="space-between">
                <div>
                  <Text fw={500}>Effective Date</Text>
                  <Text>{formatDate(insuranceInfo.effectiveDate)}</Text>
                </div>
                <div>
                  <Text fw={500}>Copay</Text>
                  <Text>${insuranceInfo.copay}</Text>
                </div>
                <div>
                  <Text fw={500}>Deductible Progress</Text>
                  <Text>${insuranceInfo.deductibleMet} / ${insuranceInfo.deductible}</Text>
                </div>
              </Group>

              <Button>Update Insurance Information</Button>
            </Stack>
          </InfoSection>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={showAddCard}
        onClose={() => setShowAddCard(false)}
        title="Add Payment Method"
        size="md"
      >
        <Stack>
          <TextInput
            label="Card Number"
            placeholder="1234 5678 9012 3456"
            required
          />
          <Group grow>
            <TextInput
              label="Expiration Date"
              placeholder="MM/YY"
              required
            />
            <TextInput
              label="CVV"
              placeholder="123"
              required
            />
          </Group>
          <TextInput
            label="Name on Card"
            placeholder="John Doe"
            required
          />
          <Group justify="space-between">
            <Button variant="subtle" onClick={() => setShowAddCard(false)}>Cancel</Button>
            <Button>Add Card</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Make Payment"
        size="md"
      >
        {selectedInvoice && (
          <Stack>
            <Text size="sm" fw={500}>Invoice Details</Text>
            <Text>Date: {formatDate(selectedInvoice.date)}</Text>
            <Text>Service: {selectedInvoice.type}</Text>
            <Text>Amount Due: ${selectedInvoice.amount.toFixed(2)}</Text>

            <Select
              label="Payment Method"
              placeholder="Select payment method"
              data={paymentMethods.map(m => ({
                value: m.id.toString(),
                label: `${m.brand} ending in ${m.last4}`
              }))}
              required
            />

            <NumberInput
              label="Payment Amount"
              defaultValue={selectedInvoice.amount}
              decimalScale={2}
              min={0}
              max={selectedInvoice.amount}
              required
            />

            <Group justify="space-between">
              <Button variant="subtle" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
              <Button>Submit Payment</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}